import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link, Outlet } from "react-router";
import { api } from "../api";
import { useAuth, useMe } from "../auth/AuthContext";
import { unreadTotal } from "../chat/chat";
import { ChatProvider, useChat } from "../chat/ChatContext";
import { useChatNotifications } from "../chat/useChatNotifications";
import { syncWebPush } from "../notifications/webPush";
import { zoneToReport } from "../stats/stats";
import { useNow } from "../useNow";

export function AppShell() {
  const { session, signOut } = useAuth();
  const me = useMe();
  // Keep this device's push registration in step with the browser's subscription.
  const deviceId = session?.deviceId;
  useEffect(() => {
    if (deviceId) void syncWebPush();
  }, [deviceId]);
  // A profile without a zone gets the browser's, so daily stats use local days; tried once per page load.
  const queryClient = useQueryClient();
  const zoneTried = useRef(false);
  const profileZone = me.data?.time_zone;
  useEffect(() => {
    if (me.data === undefined || zoneTried.current) return;
    const zone = zoneToReport(profileZone, Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (zone === null) return;
    zoneTried.current = true;
    void api.PATCH("/me", { body: { time_zone: zone } }).then(({ data }) => {
      if (data) queryClient.setQueryData(["me"], data);
    });
  }, [me.data, profileZone, queryClient]);
  return (
    <ChatProvider>
      <div className="shell">
        <header className="topbar">
          <Link to="/" className="brand">
            ten20
          </Link>
          <div className="topbar-user">
            <ChatsLink />
            <Link to="/circles" className="muted">
              Circles
            </Link>
            <Link to="/account" className="muted">
              {me.data?.name ?? "Account"}
            </Link>
            <button type="button" className="link-button" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </ChatProvider>
  );
}

/** "Chats" with the unread total (muted chats and those set to none left out), also on the app icon. */
function ChatsLink() {
  const { chat } = useChat();
  const nowMs = useNow(60_000);
  const total = chat ? unreadTotal(chat.conversations, nowMs) : 0;
  useChatNotifications(chat?.inboxLoaded ? chat.conversations : null, total);
  return (
    <Link to="/chats" className="muted topbar-chats" aria-label={total > 0 ? `Chats, ${total} unread` : undefined}>
      Chats
      {total > 0 && (
        <span className="chat-badge" aria-hidden="true">
          {total > 99 ? "99+" : total}
        </span>
      )}
    </Link>
  );
}
